import React, { useState, useEffect } from 'react';
import { db } from '../firebaseConfig';
import { doc, getDoc, updateDoc, collection, query, where, getCountFromServer, setDoc, deleteDoc, serverTimestamp, onSnapshot } from 'firebase/firestore';
import { useAuth } from '../AuthContext';
import { toast } from 'react-toastify';

function UserProfile({ userId, onClose }) {
    const { user: currentUser } = useAuth();
    const [profile, setProfile] = useState(null);
    const [stats, setStats] = useState({ hosted: 0, joined: 0 });
    const [loading, setLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [editBio, setEditBio] = useState('');
    const [socialLinks, setSocialLinks] = useState({
        website: '',
        twitter: '',
        instagram: '',
        linkedin: '',
        facebook: '',
        email: ''
    });
    const [endorsements, setEndorsements] = useState(0);
    const [hasEndorsed, setHasEndorsed] = useState(false);

    const isOwnProfile = currentUser && currentUser.uid === userId;

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const docRef = doc(db, 'users', userId);
                const docSnap = await getDoc(docRef);

                if (docSnap.exists()) {
                    const data = docSnap.data();
                    setProfile(data);
                    setEditBio(data.bio || '');
                    setSocialLinks({
                        website: data.socialLinks?.website || '',
                        twitter: data.socialLinks?.twitter || '',
                        instagram: data.socialLinks?.instagram || '',
                        linkedin: data.socialLinks?.linkedin || '',
                        facebook: data.socialLinks?.facebook || '',
                        email: data.socialLinks?.email || ''
                    });

                    // Fetch Stats
                    const hostedQuery = query(collection(db, 'sessions'), where('uid', '==', userId));
                    const hostedSnap = await getCountFromServer(hostedQuery);

                    const joinedQuery = query(collection(db, 'sessions'), where('participants', 'array-contains', userId));
                    const joinedSnap = await getCountFromServer(joinedQuery);

                    setStats({
                        hosted: hostedSnap.data().count,
                        joined: joinedSnap.data().count,
                    });

                    // Real-time listener for Endorsements
                    const unsubscribeEndorsements = onSnapshot(collection(db, 'users', userId, 'endorsements'), (snapshot) => {
                        setEndorsements(snapshot.size);
                        if (currentUser) {
                            setHasEndorsed(snapshot.docs.some(doc => doc.id === currentUser.uid));
                        }
                    });

                    return () => {
                        if (unsubscribeEndorsements) unsubscribeEndorsements();
                    };
                } else {
                    // Handle case where user is from old auth and hasn't synced yet
                    setProfile({
                        displayName: 'Unknown User',
                        photoURL: null,
                        bio: 'Profile not initialized.',
                        elo: '?'
                    });
                }
            } catch (err) {
                console.error("Error fetching profile:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchProfile();
    }, [userId]);

    const handleToggleEndorse = async () => {
        if (!currentUser) {
            toast.error("Please login to vouch for players.");
            return;
        }
        try {
            const endorsementRef = doc(db, 'users', userId, 'endorsements', currentUser.uid);
            if (hasEndorsed) {
                await deleteDoc(endorsementRef);
                toast.info("Endorsement removed.");
            } else {
                await setDoc(endorsementRef, {
                    endorserName: currentUser.displayName || 'Anonymous',
                    createdAt: serverTimestamp()
                });
                toast.success("Player endorsed!");
            }
        } catch (err) {
            console.error("Error toggling endorsement:", err);
            toast.error("Action failed.");
        }
    };

    const handleSave = async () => {
        if (!isOwnProfile) return;
        try {
            await updateDoc(doc(db, 'users', userId), {
                bio: editBio,
                socialLinks
            });
            setProfile(prev => ({ ...prev, bio: editBio, socialLinks }));
            setIsEditing(false);
            toast.success("Profile updated!");
        } catch (err) {
            console.error("Error updating profile:", err);
            toast.error("Failed to update profile.");
        }
    };

    if (loading) return <div className="p-4 text-center">Loading profile...</div>;
    if (!profile) return null;

    const earnedBadges = [];
    if (stats.hosted >= 5) earnedBadges.push({ icon: '👑', label: 'Pillar', color: 'bg-purple-100 text-purple-800 border-purple-200' });
    else if (stats.hosted >= 1) earnedBadges.push({ icon: '🎖️', label: 'Host', color: 'bg-blue-100 text-blue-800 border-blue-200' });

    if (stats.joined >= 10) earnedBadges.push({ icon: '🔥', label: 'Veteran', color: 'bg-red-100 text-red-800 border-red-200' });
    else if (stats.joined >= 1) earnedBadges.push({ icon: '♟️', label: 'Active', color: 'bg-gray-100 text-gray-800 border-gray-200' });

    if (endorsements >= 5) earnedBadges.push({ icon: '🌟', label: 'Trusted', color: 'bg-yellow-100 text-yellow-800 border-yellow-200' });

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm overflow-hidden animate-in fade-in zoom-in duration-200" onClick={e => e.stopPropagation()}>

                {/* Header / Cover */}
                <div className="h-24 bg-gradient-to-r from-indigo-500 to-purple-600 relative">
                    <button onClick={onClose} className="absolute top-2 right-2 text-white/80 hover:text-white p-2">✕</button>
                </div>

                {/* Avatar & Info */}
                <div className="px-6 pb-6 text-center -mt-12 relative">
                    <img
                        src={profile.photoURL || `https://ui-avatars.com/api/?name=${profile.displayName}&background=random`}
                        alt={profile.displayName}
                        className="w-24 h-24 rounded-full border-4 border-white shadow-md mx-auto object-cover bg-white"
                    />

                    <h2 className="text-xl font-bold text-gray-800 mt-2">{profile.displayName}</h2>
                    <div className="flex justify-center items-center gap-2 mt-1">
                        <span className="bg-yellow-100 text-yellow-800 text-xs font-bold px-2 py-0.5 rounded-full border border-yellow-200">
                            🏆 ELO {profile.elo || 1200}
                        </span>
                        <span className="text-xs text-gray-500">
                            Joined {profile.createdAt?.toDate ? profile.createdAt.toDate().toLocaleDateString() : 'recently'}
                        </span>
                    </div>

                    {earnedBadges.length > 0 && (
                        <div className="flex flex-wrap justify-center gap-1.5 mt-3 mb-1">
                            {earnedBadges.map((b, i) => (
                                <span key={i} className={`text-[10px] font-bold px-2 py-0.5 rounded-md border ${b.color} flex items-center gap-1 shadow-sm`}>
                                    <span>{b.icon}</span> {b.label}
                                </span>
                            ))}
                        </div>
                    )}

                    {isOwnProfile && !isEditing && (
                        <button
                            onClick={() => setIsEditing(true)}
                            className="mt-3 bg-indigo-100 text-indigo-700 hover:bg-indigo-200 text-xs font-bold py-1.5 px-4 rounded-full transition-colors flex items-center gap-1 mx-auto"
                        >
                            ✏️ Edit Profile
                        </button>
                    )}

                    {/* Social Links List */}
                    {Object.entries(profile.socialLinks || {}).some(([_, val]) => val) && (
                        <div className="mt-4 px-4 space-y-2">
                            {profile.socialLinks?.website && (
                                <SocialLink
                                    href={profile.socialLinks.website}
                                    icon="🌐"
                                    label="Website"
                                    color="text-indigo-600 bg-indigo-50 border-indigo-100"
                                />
                            )}
                            {profile.socialLinks?.twitter && (
                                <SocialLink
                                    href={profile.socialLinks.twitter}
                                    icon="🐦"
                                    label="Twitter / X"
                                    color="text-blue-500 bg-blue-50 border-blue-100"
                                />
                            )}
                            {profile.socialLinks?.instagram && (
                                <SocialLink
                                    href={profile.socialLinks.instagram}
                                    icon="📸"
                                    label="Instagram"
                                    color="text-pink-600 bg-pink-50 border-pink-100"
                                />
                            )}
                            {profile.socialLinks?.facebook && (
                                <SocialLink
                                    href={profile.socialLinks.facebook}
                                    icon="📘"
                                    label="Facebook"
                                    color="text-blue-700 bg-blue-50 border-blue-100"
                                />
                            )}
                            {profile.socialLinks?.linkedin && (
                                <SocialLink
                                    href={profile.socialLinks.linkedin}
                                    icon="💼"
                                    label="LinkedIn"
                                    color="text-blue-800 bg-blue-50 border-blue-100"
                                />
                            )}
                            {profile.socialLinks?.email && (
                                <a
                                    href={`mailto:${profile.socialLinks.email}`}
                                    className="flex items-center gap-3 p-2 rounded-lg border border-red-100 bg-red-50 text-red-600 hover:bg-red-100 transition-all group"
                                >
                                    <span className="text-lg">✉️</span>
                                    <span className="text-sm font-semibold">Send Email</span>
                                    <span className="ml-auto text-xs opacity-0 group-hover:opacity-100 transition-opacity">↗</span>
                                </a>
                            )}
                        </div>
                    )}

                    {/* Stats Grid */}
                    <div className="grid grid-cols-3 gap-2 mt-5 mb-5 px-2">
                        <div className="bg-gray-50 p-2 rounded-xl border border-gray-100 flex flex-col justify-center items-center shadow-sm">
                            <div className="text-xl font-bold text-indigo-600">{stats.hosted}</div>
                            <div className="text-[9px] text-gray-500 uppercase font-bold tracking-wide text-center mt-0.5">Hosted</div>
                        </div>
                        <div className="bg-gray-50 p-2 rounded-xl border border-gray-100 flex flex-col justify-center items-center shadow-sm">
                            <div className="text-xl font-bold text-indigo-600">{stats.joined}</div>
                            <div className="text-[9px] text-gray-500 uppercase font-bold tracking-wide text-center mt-0.5">Joined</div>
                        </div>
                        <div className="bg-gray-50 p-2 rounded-xl border border-gray-100 flex flex-col justify-center items-center shadow-sm relative">
                            <div className="text-xl font-bold text-gray-800 flex items-center gap-1">
                                {endorsements} <span className="text-sm">👍</span>
                            </div>
                            <div className="text-[9px] text-gray-500 uppercase font-bold tracking-wide text-center mt-0.5">Reliability</div>

                            {!isOwnProfile && currentUser && (
                                <button
                                    onClick={handleToggleEndorse}
                                    className={`absolute -bottom-3 text-[10px] font-bold px-3 py-1 rounded-full border shadow-sm transition-transform hover:scale-105 ${hasEndorsed
                                            ? "bg-green-100 text-green-700 border-green-200"
                                            : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
                                        }`}
                                >
                                    {hasEndorsed ? "✅ Vouched" : "👍 Vouch"}
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Bio Section */}
                    <div className="text-left">
                        <div className="flex justify-between items-end mb-1">
                            <label className="text-xs font-bold text-gray-500 uppercase">About</label>
                            <label className="text-xs font-bold text-gray-500 uppercase">About</label>
                        </div>

                        {isEditing ? (
                            <div className="space-y-2">
                                <textarea
                                    className="w-full border rounded p-2 text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                                    rows="3"
                                    value={editBio}
                                    onChange={(e) => setEditBio(e.target.value)}
                                    placeholder="Tell us about your chess style..."
                                />

                                <div className="grid grid-cols-2 gap-2">
                                    <input
                                        type="text"
                                        placeholder="Website (https://...)"
                                        className="border rounded p-2 text-xs focus:ring-2 focus:ring-indigo-500 outline-none"
                                        value={socialLinks.website}
                                        onChange={e => setSocialLinks(prev => ({ ...prev, website: e.target.value }))}
                                    />
                                    <input
                                        type="text"
                                        placeholder="Twitter URL"
                                        className="border rounded p-2 text-xs focus:ring-2 focus:ring-indigo-500 outline-none"
                                        value={socialLinks.twitter}
                                        onChange={e => setSocialLinks(prev => ({ ...prev, twitter: e.target.value }))}
                                    />
                                    <input
                                        type="text"
                                        placeholder="Instagram URL"
                                        className="border rounded p-2 text-xs focus:ring-2 focus:ring-indigo-500 outline-none"
                                        value={socialLinks.instagram}
                                        onChange={e => setSocialLinks(prev => ({ ...prev, instagram: e.target.value }))}
                                    />
                                    <input
                                        type="text"
                                        placeholder="LinkedIn URL"
                                        className="border rounded p-2 text-xs focus:ring-2 focus:ring-indigo-500 outline-none"
                                        value={socialLinks.linkedin}
                                        onChange={e => setSocialLinks(prev => ({ ...prev, linkedin: e.target.value }))}
                                    />
                                    <input
                                        type="text"
                                        placeholder="Facebook URL"
                                        className="border rounded p-2 text-xs focus:ring-2 focus:ring-indigo-500 outline-none"
                                        value={socialLinks.facebook}
                                        onChange={e => setSocialLinks(prev => ({ ...prev, facebook: e.target.value }))}
                                    />
                                    <input
                                        type="email"
                                        placeholder="Public Email"
                                        className="border rounded p-2 text-xs focus:ring-2 focus:ring-indigo-500 outline-none"
                                        value={socialLinks.email}
                                        onChange={e => setSocialLinks(prev => ({ ...prev, email: e.target.value }))}
                                    />
                                </div>
                                <div className="flex justify-end gap-2">
                                    <button onClick={() => setIsEditing(false)} className="text-xs text-gray-500 px-2 py-1 hover:bg-gray-100 rounded">Cancel</button>
                                    <button onClick={handleSave} className="text-xs bg-indigo-600 text-white px-3 py-1 rounded hover:bg-indigo-700">Save</button>
                                </div>
                            </div>
                        ) : (
                            <p className="text-sm text-gray-600 italic bg-gray-50 p-3 rounded border border-gray-100 min-h-[60px]">
                                {profile.bio || "No bio yet."}
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

const SocialLink = ({ href, icon, label, color }) => {
    // Ensure URL has protocol
    const safeHref = href.startsWith('http') ? href : `https://${href}`;

    return (
        <a
            href={safeHref}
            target="_blank"
            rel="noopener noreferrer"
            className={`flex items-center gap-3 p-2 rounded-lg border transition-all hover:brightness-95 group ${color}`}
        >
            <span className="text-lg">{icon}</span>
            <span className="text-sm font-semibold">{label}</span>
            <span className="ml-auto text-xs opacity-50 group-hover:opacity-100 transition-opacity">↗</span>
        </a>
    );
};

export default UserProfile;
